import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import PageHeader from "../components/PageHeader";
import CtaSection from "../components/CtaSection";
import { Reveal, RevealGroup, RevealItem } from "../components/Reveal";

const CARDS = [
  {
    to: "/tax-planning/business",
    title: "Business Tax Planning",
    body: "Organized records, preparation readiness, estimated-payment questions, business changes, and coordinating business and owner-level decisions.",
    img: "/img/ill-tax-business.webp",
    alt: "Illustration of a compass, calendar, and growing bar charts in sage green tones",
    testId: "tax-hub-business-link",
  },
  {
    to: "/tax-planning/personal",
    title: "Personal Tax Planning",
    body: "Income changes, self-employment, withholding questions, family changes, and retirement transitions. Planned, not improvised.",
    img: "/img/ill-tax-personal.webp",
    alt: "Illustration of a house, family tree, and paper plane in sage green tones",
    testId: "tax-hub-personal-link",
  },
];

export default function TaxPlanning() {
  return (
    <>
      <Seo
        title="Tax Planning for Business & Personal | Directive Optive Solutions"
        description="Business and personal tax planning conversations: preparation readiness, estimated payments, income changes, and coordinating business with household decisions."
        path="/tax-planning"
      />
      <PageHeader
        eyebrow="Tax Planning"
        title="Plan the tax year before it plans you."
        lead="Tax planning is a conversation, not a product. We look at where your records stand, what’s changing in your business or household, and what’s worth deciding early. Scope is confirmed together. This page describes the topics, not guarantees."
        image="/img/photo-sunset-summit.webp"
        imageAlt="The sun cresting a mountain ridge at golden hour"
      />

      <section className="py-24 lg:py-32" data-testid="tax-hub-cards-section">
        <div className="container-shell">
          <Reveal className="max-w-3xl">
            <h2 className="font-serif text-3xl text-olive sm:text-4xl">Two conversations, one picture.</h2>
            <p className="mt-5 text-base leading-relaxed text-moss sm:text-lg">Most owners live in both worlds. Pick the one you need today. Or bring both and we’ll coordinate them.</p>
          </Reveal>
          <RevealGroup className="mt-14 grid gap-6 md:grid-cols-2">
            {CARDS.map((c) => (
              <RevealItem key={c.to} className="h-full">
                <Link to={c.to} data-testid={c.testId} className="group flex h-full flex-col border-t-2 border-olive/25 pt-7 transition-colors duration-300 hover:border-pine">
                  <h3 className="font-serif text-2xl text-olive sm:text-3xl">{c.title}</h3>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-moss sm:text-base">{c.body}</p>
                  <span className="mt-7 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-pine">
                    Explore <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </RevealItem>
            ))}
          </RevealGroup>
          <Reveal className="mt-14 max-w-3xl rounded-md border border-olive/25 bg-sage p-8">
            <p className="text-sm leading-relaxed text-moss"><span className="font-bold text-ink">A note on scope:</span> these pages describe planning topics we discuss with clients. They are not promises of deductions, refunds, or tax savings, and nothing here is tax or legal advice. Confirmed service scope always comes first. And some situations are best served by your tax preparer or attorney, which we’ll say plainly.</p>
          </Reveal>
        </div>
      </section>

      <CtaSection heading="Let’s talk about your year ahead." />
    </>
  );
}
